import { SESSION_SAMPLED_KEY } from './constant'

/**
 * Determines if the current session should be sampled for tracking, adhering to Reapit's guidelines.
 * This simplified example uses a sampling rate. In a real application,
 * you might check for specific exception scenarios like low volume, critical features, or debugging.
 *
 * @param samplingRate The rate at which sessions should be sampled (default: 0.1, representing 10%).
 * @returns boolean indicating whether the session should be sampled (true) or not (false).
 */
export function shouldSampleSession(samplingRate: number = 0.1): boolean {
  // Adhere to Reapit's sampling guidelines:
  // - Sample in most cases to control costs.
  // - Exceptions: Very low user/event volume, feature deprecation,
  //   critical feature tracking, or debugging a live issue.
  // This simplified example uses a sampling rate.  In a real application,
  // you would check for the specific exception scenarios.
  return Math.random() < samplingRate
}

/**
 * Checks the session sampling status daily and updates it if necessary.
 * It uses `localStorage` to persist the sampling status and the date
 * the last check was performed. If it's a new day, a new sampling decision
 * is made and stored. Otherwise, the stored status for the current day is used.
 */
export function checkSessionSampling(tracesSampleRate: number): void {
  const currentDate = new Date().toDateString()
  const storedDate = localStorage.getItem('samplingDate')

  if (storedDate !== currentDate || localStorage.getItem(SESSION_SAMPLED_KEY) !== undefined) {
    // New day: reset sampling status and store the new date
    // Use environment variable for sampling rate for the session decision
    // If tracesSampleRate is 1, we sample all sessions
    // Otherwise, we use the shouldSampleSession function to determine if the session should be sampled
    const isSessionSampled = tracesSampleRate === 1 ? true : shouldSampleSession(tracesSampleRate)
    localStorage.setItem('samplingDate', currentDate)
    localStorage.setItem(SESSION_SAMPLED_KEY, JSON.stringify(isSessionSampled))
  } else {
    // Same day: use stored sampling status
    // Default to false if 'isSessionSampled' is not found in localStorage
    JSON.parse(localStorage.getItem(SESSION_SAMPLED_KEY) || 'false')
  }
}

/**
 * Checks if the event type is defined as an override, meaning it should bypass
 * the session sampling logic and always be tracked.
 *
 * @param eventType The type of event being tracked (e.g., "Override Event").
 * @returns boolean indicating whether the event type should override sampling (true) or not (false).
 */
export function shouldOverrideSampling(eventType: string): boolean {
  const overrideEventTypes = ['Override Event'] //  Example:  Events that should always be tracked
  return overrideEventTypes.includes(eventType)
}
